<template>
  <div class="cpf flex flex-col gap-5">
    <!-- Heading -->
    <div class="flex items-end justify-between gap-4">
      <div class="flex flex-col gap-1 min-w-0">
        <div class="text-xl font-semibold tracking-tight text-(--nviya-text-primary)">{{ heading }}</div>
        <div class="text-[13px] text-(--nviya-text-secondary) truncate">{{ subline }}</div>
      </div>
      <div v-if="progress" class="text-xs text-(--nviya-text-muted) tabular-nums shrink-0">{{ counts }}</div>
    </div>

    <!-- Phase strip -->
    <div class="grid grid-cols-4 gap-2">
      <div v-for="p in phases" :key="p.key" class="flex flex-col gap-2">
        <div class="h-1 rounded-full bg-(--nviya-figure-bg) overflow-hidden">
          <div
            :class="['h-full rounded-full transition-[width] duration-500',
            p.failed ? 'bg-(--nviya-critical)' : p.done ? 'bg-(--nviya-success)' : 'bg-(--nviya-accent)',
            p.indeterminate ? 'cpf-indeterminate' : '']"
            :style="{ width: p.fill }"
          ></div>
        </div>
        <div :class="['flex items-center gap-1.5 text-xs',
        p.current || p.failed ? 'font-semibold' : 'font-medium',
        p.failed ? 'text-(--nviya-critical-text)' : (p.done || p.current) ? 'text-(--nviya-text-primary)' : 'text-(--nviya-text-tertiary)']">
          <svg v-if="p.done" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--nviya-success)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7" /></svg>
          <span v-else-if="p.current" class="cpf-pulse w-[7px] h-[7px] rounded-full bg-(--nviya-accent)"></span>
          {{ p.label }}
        </div>
      </div>
    </div>

    <!-- Tiles -->
    <div class="grid grid-cols-3 sm:grid-cols-4 gap-3">
      <div v-for="(t, i) in tiles" :key="t.id" class="flex flex-col gap-1.5 min-w-0">
        <div :class="['cpf-tile relative aspect-video rounded-md overflow-hidden', `is-${t.state}`]">
          <div class="cpf-shapes absolute inset-[9px_10px] flex flex-col gap-[5px]">
            <div class="cpf-ln cpf-ink h-[5px] w-[46%] rounded-sm"></div>

            <div v-if="t.kind === 'cover'" class="flex-1 flex flex-col justify-end gap-1">
              <div class="cpf-ln cpf-strong h-[9px] w-[78%] rounded-sm"></div>
              <div class="cpf-ln cpf-ink h-1 w-[40%] rounded-sm"></div>
            </div>

            <div v-else-if="t.kind === 'chart'" class="flex-1 flex items-end gap-[5px] pt-1">
              <div v-for="(h, j) in BAR_HEIGHTS" :key="j"
                   :class="['cpf-bar flex-1 rounded-t-sm', j === 3 ? 'cpf-accent' : 'cpf-ink']"
                   :style="{ height: h }"></div>
            </div>

            <div v-else-if="t.kind === 'grid'" class="flex-1 grid grid-cols-3 gap-[3px] pt-[3px]">
              <div v-for="j in 6" :key="j" :class="['cpf-bar rounded-sm', j <= 3 ? 'cpf-head' : 'cpf-ink']"></div>
            </div>

            <div v-else class="flex-1 flex flex-col gap-1 pt-[3px]">
              <div v-for="(w, j) in LINE_WIDTHS" :key="j"
                   :class="['cpf-ln rounded-sm', j === 0 ? 'cpf-strong h-1.5' : 'cpf-ink h-1']"
                   :style="{ width: w }"></div>
            </div>
          </div>

          <div v-if="t.state === 'building'" class="cpf-sheen absolute inset-y-0 w-2/5"></div>

          <div v-if="t.state === 'built'"
               class="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full bg-(--nviya-success) flex items-center justify-center">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7" /></svg>
          </div>

          <div v-if="t.state === 'suppressed'" class="cpf-hatch absolute inset-0 flex items-center justify-center">
            <span class="text-[10px] font-medium text-(--nviya-text-muted) bg-(--nviya-surface-raised) px-1.5 py-0.5 rounded border border-(--nviya-border)">Skipped · no data</span>
          </div>

          <div v-if="t.state === 'failed'" class="absolute inset-0 flex items-center justify-center">
            <span class="text-[10px] font-semibold text-(--nviya-critical-text) bg-(--nviya-critical-bg) px-1.5 py-0.5 rounded">Slide failed</span>
          </div>
        </div>
        <div class="flex items-baseline gap-1.5 text-[11px] leading-[14px] min-w-0">
          <span class="text-(--nviya-text-tertiary) tabular-nums">{{ i + 1 }}</span>
          <span :class="['truncate',
          t.state === 'building' ? 'font-semibold text-(--nviya-accent)' :
          t.state === 'planned' ? 'font-medium text-(--nviya-text-tertiary)' :
          'font-medium text-(--nviya-text-primary)']">{{ t.title }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * The Client Pack build drawn as an abstract deck filling in.
 *
 * Driven entirely by GET /pitch-pack/sessions/{id}/progress: each tile's
 * state is what the backend builder reported for that slide, and the phase
 * strip is the runner's phase. Nothing advances on a timer — "Gathering
 * data" has no per-item progress, so it animates as indeterminate rather
 * than faking a percentage.
 */
import { computed } from 'vue'

const props = defineProps({
  // BuildProgressResponse, or null before the first poll lands
  progress: { type: Object, default: null },
})

const BAR_HEIGHTS = ['45%', '80%', '60%', '95%']
const LINE_WIDTHS = ['92%', '76%', '84%', '58%']
const PHASE_ORDER = ['assemble', 'slides', 'checks', 'saving', 'done']

// Before the first poll, draw a plausible plan-sized row of planned tiles so
// the panel does not jump when the real plan arrives.
const tiles = computed(() => props.progress?.slides
  || Array.from({ length: 13 }, (_, i) => ({ id: `p${i}`, title: '', kind: 'lines', state: 'planned' })))

const phase = computed(() => props.progress?.phase || 'queued')
const failed = computed(() => props.progress?.status === 'failed')

const phases = computed(() => {
  const p = props.progress
  const reached = failed.value ? p?.failed_phase : phase.value
  const idx = PHASE_ORDER.indexOf(reached)
  const total = p?.total || 13
  const landed = p ? p.built + p.suppressed + p.failed : 0
  return [
    { key: 'assemble', label: 'Gathering data' },
    { key: 'slides', label: 'Building slides' },
    { key: 'checks', label: 'Checks' },
    { key: 'saving', label: 'Saving' },
  ].map((ph, i) => {
    const done = phase.value === 'done' || (idx > i)
    const current = !failed.value && !done && (idx === i || (i === 0 && phase.value === 'queued'))
    const thisFailed = failed.value && idx === i
    let fill = done ? '100%' : '0%'
    let indeterminate = false
    if (current || thisFailed) {
      if (ph.key === 'slides') fill = `${Math.round((landed / total) * 100)}%`
      else { fill = '35%'; indeterminate = current }
    }
    return { ...ph, done, current, failed: thisFailed, fill, indeterminate }
  })
})

const heading = computed(() => {
  if (failed.value) return 'The build stopped'
  switch (phase.value) {
    case 'done': return 'Your deck is ready'
    case 'checks': return 'Checking the deck'
    case 'saving': return 'Saving the deck'
    case 'slides': return 'Building your deck'
    default: return 'Gathering data'
  }
})

const subline = computed(() => {
  const p = props.progress
  if (!p) return 'Starting the build'
  if (failed.value) return 'Nothing was saved. You can try again.'
  if (phase.value === 'done') return p.filename || 'Ready to download'
  if (phase.value === 'checks') return 'Layout, wording and page-number checks'
  if (phase.value === 'saving') return 'Storing the file'
  if (phase.value === 'slides') {
    const idx = p.slides.findIndex(s => s.id === p.current)
    if (idx >= 0) return `Slide ${idx + 1} of ${p.total} · ${p.slides[idx].title}`
    return `${p.built + p.suppressed + p.failed} of ${p.total} slides`
  }
  return 'Financials, the event, forecasts and market overview'
})

const counts = computed(() => {
  const p = props.progress
  const parts = [`${p.built} built`]
  if (p.suppressed) parts.push(`${p.suppressed} skipped`)
  if (p.failed) parts.push(`${p.failed} failed`)
  return parts.join(' · ')
})
</script>

<style scoped>
.cpf-tile {
  --cpf-ink: var(--nviya-border-strong);
  --cpf-head: var(--nviya-border-strong);
  --cpf-strong: var(--nviya-text-primary);
  --cpf-accent: var(--nviya-accent);
  background: var(--nviya-surface-raised);
  border: 1px solid var(--nviya-border-strong);
  transition: border-color .3s, box-shadow .3s, background-color .3s;
}
.cpf-ink { background: var(--cpf-ink); }
.cpf-head { background: var(--cpf-head); }
.cpf-strong { background: var(--cpf-strong); }
.cpf-accent { background: var(--cpf-accent); }
.cpf-shapes { transition: opacity .3s; }

.cpf-tile.is-planned {
  --cpf-ink: var(--nviya-figure-bg);
  --cpf-head: var(--nviya-figure-bg);
  --cpf-strong: var(--nviya-figure-bg);
  --cpf-accent: var(--nviya-figure-bg);
  border-style: dashed;
}
.cpf-tile.is-building {
  --cpf-ink: color-mix(in srgb, var(--nviya-accent) 22%, transparent);
  --cpf-head: color-mix(in srgb, var(--nviya-accent) 22%, transparent);
  --cpf-strong: color-mix(in srgb, var(--nviya-accent) 50%, transparent);
  --cpf-accent: color-mix(in srgb, var(--nviya-accent) 40%, transparent);
  border-color: color-mix(in srgb, var(--nviya-accent) 55%, transparent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--nviya-accent) 12%, transparent);
}
.cpf-tile.is-built { box-shadow: var(--nviya-, 0 1px 2px rgba(15, 30, 61, 0.06)); }
.cpf-tile.is-suppressed { background: var(--nviya-figure-bg); }
.cpf-tile.is-suppressed .cpf-shapes { opacity: .25; }
.cpf-tile.is-failed { border-color: var(--nviya-critical); }
.cpf-tile.is-failed .cpf-shapes { opacity: .3; }

.cpf-hatch {
  background: repeating-linear-gradient(135deg,
    color-mix(in srgb, var(--nviya-text-primary) 5%, transparent) 0 6px, transparent 6px 12px);
}
.cpf-sheen {
  background: linear-gradient(90deg, transparent,
    color-mix(in srgb, var(--nviya-surface-raised) 70%, transparent), transparent);
  animation: cpf-sheen 1.8s linear infinite;
}

.is-building .cpf-bar { transform-origin: bottom; animation: cpf-grow 1.6s ease-in-out infinite; }
.is-building .cpf-bar:nth-child(2) { animation-delay: .15s; }
.is-building .cpf-bar:nth-child(3) { animation-delay: .3s; }
.is-building .cpf-bar:nth-child(4) { animation-delay: .45s; }
.is-building .cpf-ln { transform-origin: left; animation: cpf-draw 1.8s ease-out infinite; }
.is-building .cpf-ln:nth-child(2) { animation-delay: .2s; }
.is-building .cpf-ln:nth-child(3) { animation-delay: .4s; }
.is-building .cpf-ln:nth-child(4) { animation-delay: .6s; }
.is-built .cpf-bar { transform-origin: bottom; animation: cpf-settle .45s ease-out both; }

.cpf-pulse { animation: cpf-pulse 1.6s ease-out infinite; }
.cpf-indeterminate { animation: cpf-slide 1.4s ease-in-out infinite; }

@keyframes cpf-grow { 0%, 100% { transform: scaleY(.15); } 50% { transform: scaleY(1); } }
@keyframes cpf-draw { 0% { transform: scaleX(.1); } 60%, 100% { transform: scaleX(1); } }
@keyframes cpf-settle { from { transform: scaleY(.3); } to { transform: scaleY(1); } }
@keyframes cpf-sheen { from { transform: translateX(-120%); } to { transform: translateX(260%); } }
@keyframes cpf-pulse {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--nviya-accent) 35%, transparent); }
  50% { box-shadow: 0 0 0 5px transparent; }
}
@keyframes cpf-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(290%); } }

@media (prefers-reduced-motion: reduce) {
  .cpf-sheen, .cpf-pulse, .cpf-indeterminate,
  .is-building .cpf-bar, .is-building .cpf-ln, .is-built .cpf-bar { animation: none; }
}
</style>
